<template>
    <div class="sc-message-wrapper" :class="{ repliedMessage: isRepliedMessage() }">
        <div
            :class="{
                'w-100': message.type.match(
                    /^(text|image|asset|audio|idle-time|conversation-separator|location|chatbot-image|chatbot-gallery|chatbot-button|chatbot-quick-reply|chatbot-get-location|day-separator)$/
                ),
                [message.type]: true,
                'chatbot-debug': message.type === 'chatbot-debug',
                selected: showContextMenu,
            }"
            :style="{ width: message.type === 'divider' ? 'auto' : '100%' }"
            class="sc-message"
            @contextmenu.prevent.stop="openContextMenu"
        >
            <div
                :class="{
                    sent: !replyMode && message.position === 'right',
                    received: !replyMode && message.position !== 'right' && message.type !== 'system',
                    system: !replyMode && message.type === 'system',
                }"
                class="sc-message--content"
                ref="contextWrapper"
            >
                <slot :message="message" :user="user" name="user-avatar">
                    <Avatar
                        v-if="showAvatar && message.type !== 'system' && authorName !== undefined"
                        :size="30"
                        :username="authorName"
                        :src="getAgentImage"
                        style="color: white; font-size: 15px; margin-right: 15px; cursor: default"
                        v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                        :content="authorName"
                    />
                </slot>
                <DailySeparator v-if="message.type === 'day-separator'" :created-date="message.raw.createdDate" />

                <DeletedInfoMessage
                    v-if="$_.get(message, 'raw.isDeleted')"
                    :is-chat-window="isChatWindow"
                    :extended-style="extendedStyle()"
                    :is-colored="isColored && isRight"
                    :message="message"
                    :messageColors="determineMessageColors"
                    :disable-translation="disableTranslation"
                    @remove="$emit('remove')"
                    :reply-mode="replyMode"
                />
                <TextMessage
                    v-else-if="message.type === 'text'"
                    :is-chat-window="isChatWindow"
                    :is-private-message="isPrivateMessage"
                    :extended-style="extendedStyle()"
                    :is-colored="isColored && isRight"
                    :message="message"
                    :messageColors="determineMessageColors"
                    :disable-translation="disableTranslation"
                    @remove="$emit('remove')"
                    :reply-mode="replyMode"
                    :visitorId="visitorId"
                >
                    <template v-slot:default="scopedProps">
                        <slot
                            :me="scopedProps.me"
                            :message="scopedProps.message"
                            :messageColors="scopedProps.messageColors"
                            :messageText="scopedProps.messageText"
                            name="text-message-body"
                        >
                        </slot>
                    </template>
                    <template v-slot:text-message-toolbox="scopedProps">
                        <slot
                            :me="scopedProps.me"
                            :message="scopedProps.message"
                            name="text-message-toolbox"
                            style="margin-bottom: 0 !important"
                        >
                        </slot>
                    </template>
                </TextMessage>
                <VoiceMessage
                    v-else-if="isAudioMessage()"
                    :data="message.raw"
                    :chat-message="message"
                    @open-gallery="$emit('open-gallery', $event)"
                />
                <VideoMessage v-else-if="isVideoMessage()" :data="message.data" :message="message" v-once @open-gallery="$emit('open-gallery', $event)" />
                <ImageMessage
                    v-once
                    v-else-if="message.type === 'image'"
                    :extended-style="extendedStyle()"
                    :message="message"
                    :message-colors="determineMessageColors"
                    :received="!replyMode && message.position !== 'right' && message.type !== 'system'"
                    @open-gallery="$emit('open-gallery', $event)"
                />
                <AssetMessage
                    v-else-if="message.type === 'asset'"
                    :message="message"
                    :extended-style="extendedStyle()"
                    :message-colors="determineMessageColors"
                    @open-gallery="$emit('open-gallery', $event)"
                />
                <EmojiMessage v-else-if="message.type === 'emoji'" :data="message.data" />
                <FileMessage
                    v-else-if="message.type === 'file'"
                    :data="message.data"
                    :messageColors="determineMessageColors"
                />
                <TypingMessage v-else-if="message.type === 'typing'" :messageColors="determineMessageColors" />
                <IdleIndicatorMessage
                    v-else-if="message.type === 'idle-time'"
                    :start-timestamp="message.data.startTimestamp"
                    :is-live="message.data.isLive"
                />
                <ChatSeparatorMessage
                    v-else-if="message.type === 'conversation-separator'"
                    :created-date="message.raw.createdDate"
                />
                <LoadMoreMessage
                    v-else-if="message.type === 'load-more'"
                    :conversation-id="getConversationId()"
                    :contact-id="visitorId"
                />
                <VoiceAgentCallMessage
                    v-else-if="message.type === 'voice-agent-call'"
                    :message="message"
                    :messageColors="determineMessageColors"
                />
                <SystemMessage
                    v-if="message.type === 'system' && shouldShowSystem"
                    :data="message.data"
                    :messageColors="systemMessageColorsStyle()"
                >
                    <slot :message="message.data" name="system-message-body"></slot>
                </SystemMessage>
                <a-divider v-else-if="message.type === 'divider'" class="daysDivider">
                    {{ message.data.meta.split(" - ")[0] }}
                </a-divider>
                <LocationMessage v-else-if="message.type === 'location'" :message="message" />

                <BotImageMessage v-else-if="message.type === 'chatbot-image'" :data="message" @open-gallery="$emit('open-gallery', $event)" />
                <BotFileMessage
                    v-else-if="message.type === 'chatbot-file'"
                    :data="message"
                    :extended-style="extendedStyle()"
                    :message-colors="determineMessageColors"
                    @open-gallery="$emit('open-gallery', $event)"
                />
                <BotGalleryMessage
                    v-else-if="message.type === 'chatbot-gallery'"
                    :data="message.raw.payload"
                    :conversation-id="getConversationId()"
                    :last-clicked-button="message.raw.lastClickedButton"
                />
                <BotButtonMessage
                    v-else-if="message.type === 'chatbot-button'"
                    :data="message.raw.payload"
                    :conversation-id="getConversationId()"
                    :last-clicked-button="message.raw.lastClickedButton"
                />

                <GetLocationMessage
                    v-else-if="message.type === 'chatbot-get-location'"
                    :message="message"
                    :conversation-id="getConversationId()"
                />

                <SelectReservationDay
                    v-else-if="message.type === 'select-reservation-day'"
                    :data="message.raw.payload"
                />

                <MailMessage v-else-if="message.type === 'user-email'" :message="message" />
                <SMSMessage v-else-if="message.type === 'user-sms'" :message="message" />

                <BotQuickReplyMessage
                    v-else-if="message.type === 'chatbot-quick-reply'"
                    :messageColors="determineMessageColors"
                    :extended-style="extendedStyle()"
                    :is-colored="isColored && isRight"
                    :data="message.raw.payload"
                    :message="message"
                    :meta="message.data.meta"
                    :meta2="message.data.meta2"
                    :conversation-id="getConversationId()"
                    :last-clicked-button="message.raw.lastClickedButton"
                />
                <ChatbotDebugMessage
                    v-else-if="message.type === 'chatbot-debug' && debugModeEnabled"
                    :message="message"
                />
                <WABATemplateMessage
                    v-else-if="message.type === 'whatsapp-business-api-template'"
                    :data="message.raw"
                    :conversation-id="getConversationId()"
                    :last-clicked-button="message.raw.lastClickedButton"
                />
                <MetaPostMessage
                    v-else-if="message.type === 'meta-post'"
                    :data="message.raw"
                    :conversation-id="getConversationId()"
                    :last-clicked-button="message.raw.lastClickedButton"
                />

                <TrendyolProductMessage
                    v-else-if="message.type === 'trendyol-product'"
                    :data="message.raw"
                    :conversation-id="getConversationId()"
                />

                <JsonMessage
                    v-else-if="message.type === 'json'"
                    :message="message"
                    :extended-style="extendedStyle()"
                    :message-colors="determineMessageColors"
                    :reply-mode="replyMode"
                    :right="message.position === 'right'"
                />

                <!-- Message Reactions -->
                <div
                    v-if="hasReactions"
                    class="message-reactions"
                    :class="{
                        'reactions-left': message.position === 'right',
                        'reactions-right': message.position !== 'right',
                    }"
                    @click="openReactionModal"
                    v-tippy="{ followCursor: true, placement: 'top' }"
                    :content="getTotalReactionsTooltip()"
                >
                    <span v-for="emoji in getTopReactions" :key="emoji" class="reaction-emoji">
                        {{ emoji }}
                    </span>
                    <span class="reaction-total-count" v-if="totalReactionsCount > 2">{{ totalReactionsCount }}</span>
                </div>

                <!-- Message Edited Indicator -->
                <div
                    v-if="isMessageEdited"
                    class="message-edited-indicator"
                    :class="{
                        'edited-right': message.position === 'right',
                        'edited-left': message.position !== 'right',
                    }"
                >
                    <i class="material-icons">edit</i>
                    <span class="edited-text">{{ getEditedText() }}</span>
                </div>

                <span
                    class="checkMark"
                    v-if="message.position === 'right' && !shouldDismissMessage && message.type !== 'chatbot-debug' && !isPrivateMessage"
                    :style="{
                        color: $_.get(message, 'raw.status', 'sent') === 'pending' ? '#ffc500' : 'lime',
                    }"
                    >✓</span
                >
                <div
                    @click="removePendingMsg(message.raw.uniqMessageId)"
                    class="notSend"
                    v-if="
                        shouldDismissMessage &&
                        isRight &&
                        $_.get(message, 'raw.status', 'sent') === 'pending' &&
                        ['text', 'audio', 'image', 'asset', 'emoji', 'file', 'location'].includes(message.type)
                    "
                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                    :content="$t('message.removePendingMessage')"
                >
                    x
                </div>
            </div>
            <div
                v-if="showFallbackMeta && fallbackMetaLineText"
                class="sc-message-fallback-meta"
                :class="{
                    'fallback-meta-right': message.position === 'right',
                    'fallback-meta-left': message.position !== 'right',
                }"
            >
                <span class="fallback-meta-text" v-tippy="fallbackMetaTippyOptions">{{ fallbackMetaLineText }}</span>
            </div>
            <div class="ai-model-info" v-if="aiModelText">
                <i class="material-icons">auto_awesome</i>
                <span>{{ aiModelText }}</span>
            </div>
            <span
                v-if="whatsappDeliveryStatus().visible && !isPrivateMessage"
                class="wp-delivery"
                :class="{ 'wp-delivery-left': message.position !== 'right' }"
                @click="handleErrorClick"
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                content="Whatsapp Hata Mesajları hakkında detaylı bilgi almak için tıklayınız."
            >
                <WhatsappIcon
                    class="wp-delivery-icon"
                    :color="
                        whatsappDeliveryStatus().pending ? 'gray' : whatsappDeliveryStatus().delivered ? 'green' : 'red'
                    "
                />
                <span
                    class="delivery-status"
                    :style="{
                        color: whatsappDeliveryStatus().pending
                            ? 'gray'
                            : whatsappDeliveryStatus().delivered
                            ? 'green'
                            : 'red',
                    }"
                >
                    {{ whatsappDeliveryStatus().tooltip }}
                </span>
                <i class="resent-label" v-if="pendingWhatsappMessages[$_.get(message, 'raw.uniqMessageId')]">
                    Gönderiliyor
                </i>
            </span>
            <!-- Meta (Instagram/Messenger) Delivery Status -->
            <span
                v-if="metaDeliveryStatus().visible && !isPrivateMessage"
                class="wp-delivery"
                :class="{ 'wp-delivery-left': message.position !== 'right' }"
                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                :content="metaDeliveryStatus().tooltip"
            >
                <InstagramIcon
                    v-if="metaDeliveryStatus().platform === 'instagram'"
                    class="wp-delivery-icon instagram"
                    :color="metaDeliveryStatus().pending ? 'gray' : metaDeliveryStatus().delivered ? 'green' : 'red'"
                />
                <FacebookIcon
                    v-else-if="metaDeliveryStatus().platform === 'messenger'"
                    class="wp-delivery-icon"
                    :color="metaDeliveryStatus().pending ? 'gray' : metaDeliveryStatus().delivered ? 'green' : 'red'"
                />
                <span
                    class="delivery-status"
                    :style="{
                        color: metaDeliveryStatus().pending ? 'gray' : metaDeliveryStatus().delivered ? 'green' : 'red',
                    }"
                >
                    {{ metaDeliveryStatus().tooltip }}
                </span>
            </span>
        </div>
        <context-menu
            v-if="(contextMenu || hasActorDetail) && isContextEnabled()"
            class="context-menu"
            :style="{
                top: `${contextMenuPosition.y}px !important`,
                left: `${contextMenuPosition.x}px !important`,
                display: showContextMenu ? 'block' : 'none',
            }"
            :target="contextMenuTarget"
            :show="showContextMenu"
            @update:show="(show) => (showContextMenu = show)"
            @contextmenu.prevent="openContextMenu"
            @dblclick.prevent=""
        >
            <div class="context-menu-content">
                <div class="item" @click="goToActorBlock" v-if="hasActorDetail">
                    <span class="material-icons"> location_searching </span>
                    <span>{{ actorBlockText }}</span>
                </div>
                <div class="item" @click="() => downloadFile()" v-if="!contextMenuOnlyCreateTask && isDownloadEnabled()">
                    <span class="material-icons"> download </span>
                    <span>{{ $t("tables.download") }}</span>
                </div>
                <div class="item" @click="() => onContextMenuSelected('copy')" v-if="!contextMenuOnlyCreateTask && isCopyEnabled()">
                    <span class="material-icons"> content_copy </span>
                    <span v-if="message.type === 'image'">{{ $t("message.copyImage") }}</span>
                    <span v-else>{{ $t("message.copyText") }}</span>
                </div>
                <div class="item" @click="() => onContextMenuSelected('reply')" v-if="!contextMenuOnlyCreateTask && !isCommentMessage()">
                    <span class="material-icons"> reply </span>
                    <span>{{ $t("message.replyMessage") }}</span>
                </div>
                <div class="item" @click="createTask">
                    <span class="material-icons"> add_task </span>
                    <span>{{ $t("task.createTask") }}</span>
                </div>
                <div class="item" @click="newEventModalVisible = true" v-if="!contextMenuOnlyCreateTask">
                    <span class="material-icons"> edit_calendar </span>
                    <span>{{ $t("message.createEvent") }}</span>
                </div>
                <div class="item" @click="onContextMenuSelected('translate')" v-if="!contextMenuOnlyCreateTask && isTranslateActive()">
                    <span class="material-icons"> translate </span>
                    <span>{{ $t("message.translateVisitorMessage") }}</span>
                </div>

                <!-- Instagram/Messenger Comment Actions -->
                <div class="item" @click="replyCommentAction" v-if="!contextMenuOnlyCreateTask && isCommentMessage()">
                    <span class="material-icons"> reply </span>
                    <span>Yorumu Yanıtla</span>
                </div>

                <!-- Kategorili otomasyonlar (önce bunlar) -->
                <template v-if="!contextMenuOnlyCreateTask">
                    <div
                        class="item submenu-item"
                        v-for="category in categorizedMessageAutomations"
                        :key="category.name"
                        @mouseenter="handleSubmenuHover($event, category.name)"
                        @mouseleave="handleSubmenuLeave"
                    >
                    <i
                        v-if="category.icon"
                        class="material-icons myIcon"
                        :style="{ color: 'var(--color-icon-primary-default)' }"
                    >
                        {{ category.icon }}
                    </i>
                    <i v-else class="material-icons myIcon" :style="{ color: 'var(--color-icon-primary-default)' }">
                        folder
                    </i>
                    <span>{{ category.name }}</span>
                    <i class="material-icons submenu-arrow">chevron_right</i>

                    <!-- Sub menü -->
                    <div
                        v-if="currentSubmenuPosition[category.name]"
                        class="submenu-content"
                        :style="currentSubmenuPosition[category.name]"
                        @mouseenter="cancelSubmenuLeave"
                        @mouseleave="handleSubmenuLeave"
                    >
                        <div class="submenu-inner">
                            <div
                                class="item"
                                v-for="automation in category.automations"
                                :key="automation._id"
                                @click.stop="() => runAutomation(automation)"
                            >
                                <i
                                    class="material-icons myIcon"
                                    :style="{ color: 'var(--color-icon-primary-default)' }"
                                >
                                    power_settings_new
                                </i>
                                <span>{{ automation.name }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                </template>

                <!-- Ayırıcı (eğer hem kategoriler hem de genel otomasyonlar varsa) -->
                <div
                    v-if="!contextMenuOnlyCreateTask && categorizedMessageAutomations.length > 0 && generalMessageAutomations.length > 0"
                    class="item-divider"
                ></div>

                <!-- Genel kategorideki otomasyonlar (sonra bunlar) -->
                <template v-if="!contextMenuOnlyCreateTask">
                    <div
                        class="item"
                        v-for="automation in generalMessageAutomations"
                        :key="automation._id"
                        @click="() => runAutomation(automation)"
                    >
                        <i class="material-icons myIcon" :style="{ color: 'var(--color-icon-primary-default)' }">
                            power_settings_new
                        </i>
                        <span>{{ automation.name }}</span>
                    </div>
                </template>
            </div>
        </context-menu>

        <div
            v-if="message.type === 'conversation-separator' && showSeparatorContextMenu"
            class="separator-context-menu"
            :style="{
                top: `${separatorContextMenuPosition.y}px`,
                left: `${separatorContextMenuPosition.x}px`,
            }"
            @mouseleave="showSeparatorContextMenu = false"
        >
            <div class="item" @click="copyChatId">
                <span class="material-icons">content_copy</span>
                <span>{{ $t("message.copyChatId") }}</span>
            </div>
        </div>

        <a-divider v-if="isNewMessage(message)" class="newMessageDivider"
            >{{ $t("message.unreadMessages") }}
        </a-divider>
        <CreateEventModal
            :eventSummary="message.data.text"
            :calendarList="calendarList"
            v-if="newEventModalVisible"
            @close="newEventModalVisible = false"
        />
        <ZohoRecordPicker
            v-if="showZohoRecordPicker"
            :phone="activeContactPhone"
            :module="zohoModule"
            @selected="handleZohoRecordSelected"
            @cancel="handleZohoRecordPickerCancel"
            :contactId="activeContactZohoContactId"
        />

        <!-- Reaction Details Modal -->
        <Modal2
            v-if="showReactionModal"
            title="Reaksiyonlar"
            :show-footer="false"
            @cancel="showReactionModal = false"
            medium
        >
            <div class="reactions-modal-content">
                <div v-for="(reactionGroup, emoji) in groupedReactions" :key="emoji" class="reaction-group">
                    <div class="reaction-group-header">
                        <span class="reaction-group-emoji">{{ emoji }}</span>
                        <span class="reaction-group-count">{{ reactionGroup.length }}</span>
                    </div>
                    <div class="reaction-users-list">
                        <div v-for="reaction in reactionGroup" :key="reaction.author" class="reaction-user">
                            <div class="reaction-user-info">
                                <span class="reaction-user-name">{{ reaction.authorName || "Bilinmeyen" }}</span>
                                <span class="reaction-timestamp">{{ formatReactionTime(reaction.timestamp) }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal2>

        <!-- Add Modal -->
        <Modal2
            v-if="showErrorModal"
            :title="$t('auto.whatsappErrorDetailsTitle')"
            :show-footer="false"
            @cancel="showErrorModal = false"
            xlarge
        >
            <WABAErrorCodes :default-selected-error="currentError" />
        </Modal2>
    </div>
</template>

<script>
import TextMessage from "./messages/TextMessage.vue";
import MailMessage from "./messages/MailMessage.vue";
import SMSMessage from "./messages/SMSMessage.vue";
import FileMessage from "./messages/FileMessage.vue";
import EmojiMessage from "./messages/EmojiMessage.vue";
import TypingMessage from "./messages/TypingMessage.vue";
import IdleIndicatorMessage from "./messages/IdleIndicatorMessage.vue";
import ChatSeparatorMessage from "./messages/ChatSeparatorMessage.vue";
import LoadMoreMessage from "./messages/LoadMoreMessage.vue";
import SystemMessage from "./messages/SystemMessage.vue";
import DailySeparator from "./messages/DailySeparator.vue";

import Avatar from "vue-avatar";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import VoiceMessage from "./messages/VoiceMessage";
import ImageMessage from "./messages/ImageMessage";
import AssetMessage from "./messages/AssetMessage";
import LocationMessage from "./messages/LocationMessage";
import moment from "moment";
import _ from "lodash";
import BotGalleryMessage from "./messages/chatbot/BotGalleryMessage";
import BotButtonMessage from "./messages/chatbot/BotButtonMessage";
import BotQuickReplyMessage from "./messages/chatbot/BotQuickReplyMessage";
import BotImageMessage from "./messages/chatbot/BotImageMessage";
import BotFileMessage from "./messages/chatbot/BotFileMessage";
import VideoMessage from "./messages/VideoMessage";
import GetLocationMessage from "./messages/chatbot/GetLocationMessage";
import SelectReservationDay from "./messages/chatbot/SelectReservationDay";
import { sendSocketMessage, socketMessageStream } from "../../utils/socketStream";
import { filter, first, take, timeout } from "rxjs/operators";
import { copyImage2Clipboard, copyText2Clipboard } from "../../utils/utils";
import { toNewMessagesModel } from "../../utils/chat";
import WABATemplateMessage from "./messages/WABATemplateMessage.vue";
import CreateEventModal from "../../views/Pages/Calendar/Component/CreateEventModal.vue";
import MetaPostMessage from "./messages/MetaPostMessage.vue";
import DeletedInfoMessage from "./messages/DeletedInfoMessage.vue";
import WhatsappIcon from "../../views/Pages/Chatbot/pages/integrations/svg/WhatsappIcon.vue";
import InstagramIcon from "../../views/Pages/Chatbot/pages/integrations/svg/InstagramIcon.vue";
import FacebookIcon from "../../views/Pages/Chatbot/pages/integrations/svg/FacebookIcon.vue";
import TrendyolProductMessage from "./messages/TrendyolProductMessage.vue";
import ZohoRecordPicker from "./chat-window/zoho/ZohoRecordPicker.vue";
import Modal2 from "../common/Modal2.vue";
import WABAErrorCodes from "./components/WABAErrorCodes.vue";
import VoiceAgentCallMessage from "./messages/VoiceAgentCallMessage.vue";
import JsonMessage from "./messages/JsonMessage.vue";
import ChatbotDebugMessage from "./messages/ChatbotDebugMessage.vue";

export default {
    data() {
        return {
            shouldDismissMessage: false,
            pendingWhatsappMessages: {},
            resentWhatsappMessageStatus: {},
            contextMenuPosition: { x: 0, y: 0 },
            showContextMenu: false,
            contextMenuTarget: this.$refs.contextWrapper,
            showSeparatorContextMenu: false,
            separatorContextMenuPosition: { x: 0, y: 0 },
            toNewMessagesModel,
            userList: [],
            newEventModalVisible: false,
            showZohoRecordPicker: false,
            currentAutomation: null,
            zohoModule: null,
            showErrorModal: false,
            currentError: null,
            showReactionModal: false,
            currentSubmenuPosition: {},
            submenuLeaveTimeout: null,
        };
    },
    components: {
        WhatsappIcon,
        InstagramIcon,
        FacebookIcon,
        MetaPostMessage,
        TrendyolProductMessage,
        WABATemplateMessage,
        CreateEventModal,
        GetLocationMessage,
        BotQuickReplyMessage,
        BotButtonMessage,
        BotGalleryMessage,
        BotImageMessage,
        BotFileMessage,
        LocationMessage,
        AssetMessage,
        ImageMessage,
        VoiceMessage,
        VideoMessage,
        TextMessage,
        FileMessage,
        EmojiMessage,
        TypingMessage,
        IdleIndicatorMessage,
        ChatSeparatorMessage,
        LoadMoreMessage,
        SystemMessage,
        Avatar,
        DailySeparator,
        MailMessage,
        SMSMessage,
        DeletedInfoMessage,
        SelectReservationDay,
        ZohoRecordPicker,
        Modal2,
        WABAErrorCodes,
        VoiceAgentCallMessage,
        JsonMessage,
        ChatbotDebugMessage,
    },
    props: {
        visitorId: {
            type: String,
            required: false,
        },
        isChatWindow: { type: Boolean, default: false },
        isPrivateChat: { type: Boolean, default: false },
        replyMode: {
            type: Boolean,
            default: false,
        },
        quoteMode: {
            type: Boolean,
            default: false,
        },
        disableTranslation: {
            type: Boolean,
            default: false,
            required: false,
        },
        message: {
            type: Object,
            required: true,
        },
        colors: {
            type: Object,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
        showAvatar: {
            type: Boolean,
            required: false,
            default: false,
        },
        randomBgColorWithName: {
            type: Boolean,
            required: false,
            default: false,
        },
        isColored: {
            type: Boolean,
            default: false,
            required: false,
        },
        isWhatsapp: {
            type: Boolean,
            default: false,
            required: false,
        },
        channelId: {
            type: String,
            required: false,
        },
        overrideStyle: {
            type: Object,
            required: false,
        },
        contextMenu: {
            type: Boolean,
            default: true,
            required: false,
        },
        /** Archives gibi sayfalarda sadece "Görev oluştur" seçeneğinin görünmesi için true verilir */
        contextMenuOnlyCreateTask: {
            type: Boolean,
            default: false,
            required: false,
        },
        userMessageColor: {
            type: Object,
            required: false,
            default: () => ({
                color: "#000",
                backgroundColor: "#F2EDFF",
            }),
        },
        debugModeEnabled: {
            type: Boolean,
            default: false,
            required: false,
        },
    },
    computed: {
        ...mapGetters({
            isDarkMode: "user/isDarkMode",
            storeContacts: "contact/storeContacts",
        }),
        isPrivateMessage() {
            return ["chatbot-private", "private"].includes(this.message.raw?.messageType);
        },
        ...mapState({
            calendarList: (state) => state.calendar.calendarList,
            beforeLastPrivateChat: (state) => state.app.beforeLastPrivateChat,
            isPrivateChatPositionLast: (state) => state.app.isPrivateChatPositionLast,
            siteId: (state) => state.site.site._id,
            authUser: (state) => state.auth.user,
            selectedChat: (state) => state.team.selectedChat,
            channels: (state) => state.channel.channels,
            users: (state) =>
                state.user.users.data.reduce((a, b) => {
                    a[b._id] = b;
                    return a;
                }, {}),
            chats: (state) => state.team.chats,
            settings: (state) => state.setting.settings,
            replyToMessages: (state) => state.chat.pinnedReplyToMessages,
            allMessageAutomations: (state) =>
                state.automation.automations.filter((a) => a.options?.event?.type === "message.picked"),
            zohoSelectedDeals: (state) => state.zoho.selectedDeals,
            autoTranslate: (state) => state.chat.autoTranslate,
            selectedContact: (state) => state.contact.selectedContact,
        }),
        messageAutomations() {
            const currentUserId = this.authUser?._id;
            const userDepartments = this.authUser?.subjects || [];

            return this.allMessageAutomations.filter((automation) => {
                // executionPrivacy tanımlıysa ve kullanıcı/departman kısıtlaması varsa kontrol et
                if (automation.executionPrivacy) {
                    const allowedUsers = automation.executionPrivacy.users || [];
                    const allowedDepartments = automation.executionPrivacy.departments || [];

                    // Eğer hem users hem de departments boşsa, herkese açık demektir
                    if (allowedUsers.length === 0 && allowedDepartments.length === 0) {
                        return true;
                    }

                    // Kullanıcı izin listesinde mi?
                    const hasUserPermission = allowedUsers.some((userId) => userId === currentUserId);

                    // Kullanıcının departmanı izin listesinde mi?
                    const hasDepartmentPermission =
                        allowedDepartments.some((deptId) => userDepartments.includes(deptId)) ||
                        userDepartments.length === 0;

                    return hasUserPermission || hasDepartmentPermission;
                }

                return true;
            });
        },
        generalMessageAutomations() {
            return this.messageAutomations.filter(
                (x) => !x.category || !x.category.name || x.category.name === "Genel"
            );
        },
        categorizedMessageAutomations() {
            const categories = {};

            this.messageAutomations.forEach((automation) => {
                const categoryName = automation.category?.name;
                if (categoryName && categoryName !== "Genel") {
                    if (!categories[categoryName]) {
                        categories[categoryName] = {
                            name: categoryName,
                            icon: automation.category?.icon,
                            automations: [],
                        };
                    }
                    categories[categoryName].automations.push(automation);
                }
            });

            return Object.values(categories).sort((a, b) => a.name.localeCompare(b.name));
        },
        shouldShowSystem() {
            const agentMsgList = ["user-login", "user-destroy", "user-leave", "custom"];
            if (
                agentMsgList.includes(this.message.raw?.systemMessageType) &&
                this.settings.disableAgentSystemMessages
            ) {
                return false;
            }
            return true;
        },

        authorName() {
            return this.user && this.user.name && this.users[this.user._id] === undefined
                ? this.user.name
                : this.users[this.user?._id]?.name;
        },
        getAgentImage() {
            return this.users[this.user?._id]?.gravatar;
        },
        isRight() {
            return this.message.position === "right";
        },
        activeContactPhone() {
            let number =
                this.contact?.phone ||
                this.contact?.whatsappNumber ||
                "";
            number = number.replace(/[^0-9]/g, "");
            return number;
        },
        activeContactZohoContactId() {
            return this.contact?.zohoInfo?.contactId;
        },
        hasReactions() {
            return this.message.raw?.reactions && this.message.raw.reactions.length > 0;
        },
        groupedReactions() {
            if (!this.hasReactions) return {};

            const grouped = {};
            this.message.raw.reactions.forEach((reaction) => {
                const emoji = reaction.reaction;
                if (!grouped[emoji]) {
                    grouped[emoji] = [];
                }
                grouped[emoji].push(reaction);
            });

            return grouped;
        },
        getTopReactions() {
            // En çok kullanılan ilk 2 farklı emoji'yi al
            const sortedReactions = Object.keys(this.groupedReactions)
                .sort((a, b) => this.groupedReactions[b].length - this.groupedReactions[a].length)
                .slice(0, 2);

            return sortedReactions;
        },
        totalReactionsCount() {
            return this.message.raw?.reactions?.length || 0;
        },
        isMessageEdited() {
            return this.message.raw?.isEdited === true;
        },
        contact(){
            let selectedContact = this.selectedContact;
            if(!selectedContact){
                selectedContact = this.storeContacts(this.visitorId);
            }
            return selectedContact;
        },
        hasActorDetail() {
            return !!this.message.raw?.actorDetail;
        },
        actorBlockText() {
            const detail = this.message.raw?.actorDetail;
            if (detail?.type === "automation") return this.$t("message.goToAutomation");
            if (detail?.type === "chatbot") return this.$t("message.goToChatbotBlock");
            return this.$t("message.goToMessageDetail");
        },
        aiModelText() {
            const detail = this.message.raw?.actorDetail;
            if (detail?.aiModel) {
                return this.$t("message.generatedByAI", { model: detail.aiModel });
            }
            return null;
        },
        showFallbackMeta() {
            if (this.replyMode) return false;
            if (_.get(this.message, "raw.isDeleted")) return false;
            var type = this.message.type;
            var skipTypes = [
                "text",
                "file",
                "json",
                "chatbot-image",
                "chatbot-file",
                "chatbot-quick-reply",
                "chatbot-debug",
                "whatsapp-business-api-template",
                "meta-post",
                "voice-agent-call",
                "day-separator",
                "conversation-separator",
                "system",
                "divider",
                "typing",
                "idle-time",
                "load-more",
            ];
            if (skipTypes.indexOf(type) !== -1) return false;
            if (type === "asset") {
                var audioMime = _.get(this.message, "raw.asset.mime", "") || "";
                if (audioMime.indexOf("audio") !== -1) return true;
                if (this.message.isMail) return true;
                return false;
            }
            if (this.isVideoMessage()) return false;
            return true;
        },
        fallbackMetaLineText() {
            if (!this.showFallbackMeta) return "";
            var meta = _.get(this.message, "data.meta");
            if (meta !== undefined && meta !== null && String(meta).replace(/^\s+|\s+$/g, "") !== "") {
                return this.metaShorter(String(meta).replace(/^\s+|\s+$/g, ""));
            }
            var d = _.get(this.message, "raw.createdDate") || _.get(this.message, "data.startTimestamp");
            if (!d) return "";
            return moment(d).format("HH:mm");
        },
        fallbackMetaTooltip() {
            var d = _.get(this.message, "raw.createdDate") || _.get(this.message, "data.startTimestamp");
            if (d) {
                return new Date(d).toLocaleString("tr-TR", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                });
            }
            var meta = _.get(this.message, "data.meta");
            if (meta !== undefined && meta !== null && String(meta).replace(/^\s+|\s+$/g, "") !== "") {
                return String(meta).replace(/^\s+|\s+$/g, "");
            }
            return "";
        },
        fallbackMetaTippyOptions() {
            var content = this.fallbackMetaTooltip || this.fallbackMetaLineText;
            if (!content) return {};
            return {
                content: content,
                followCursor: true,
                placement: "top",
            };
        },
        // Reactive: messageType / selectedChat / message.raw değişince otomatik olarak yeniden hesaplanır.
        // Method olarak çağrıldığında Vue bağımlılıkları takip edemediği için anlık renk güncellemeleri kaçırılıyordu.
        determineMessageColors() {
            const me = this.isRight;
            const isPM = this.message.raw && ["chatbot-private", "private"].includes(this.message.raw.messageType);
            // Private mesajlar her durumda (aktif/pasif sohbet, conversation id formatından bağımsız)
            // sarı arka plan ile gösterilmeli. Bu kontrol diğer tüm kontrollerden önce yapılmalı.
            if (isPM) {
                document.documentElement.style.setProperty("--tail-color", this.privateColorsStyle().backgroundColor);
                return this.privateColorsStyle();
            }
            const chatType = this.selectedChat?.meta?.chatType;
            const isTeam = chatType === "team" || chatType === "department" || chatType === "groupThread";
            if (
                this.message.raw?.conversation === this.selectedChat?._id ||
                this.message.raw?.conversationId === this.selectedChat?._id
            ) {
                if (isTeam) {
                    document.documentElement.style.setProperty("--tail-color", this.sentColorsStyle().backgroundColor);
                    return me ? this.sentColorsStyle() : this.receivedColorsStyle();
                }
                document.documentElement.style.setProperty(
                    "--tail-color",
                    this.standartColorStyle().backgroundColor
                );
                return me ? this.standartColorStyle() : this.receivedColorsStyle();
            }
            document.documentElement.style.setProperty("--tail-color", this.standartColorStyle().backgroundColor);
            return me ? this.standartColorStyle() : this.receivedColorsStyle();
        },
    },
    methods: {
        ...mapActions({
            pinReplyToMessage: "chat/pinReplyToMessage",
            addNewWindow: "app/addNewWindow",
        }),
        metaShorter(text) {
            if (text === undefined || text === null || text === "") return "";
            var s = String(text);
            var delimiter = " - ";
            var t = s.split(delimiter);
            if (t[0].length > 11) t[0] = t[0].slice(0, 11) + "..";
            return t.join(delimiter);
        },
        isTranslateActive() {
            if (!this.isChatWindow || this.isPrivateChat) {
                return false;
            }
            const contact = this.contact;
            const alreadyExists = (this.message.raw.translates || []).find(
                (t) => t.to === this.authUser.lang && t.from === contact?.preferredLang
            );
            return (
                contact &&
                this.message.type === "text" &&
                this.autoTranslate.includes(contact?.lastConversation._id) &&
                contact?.preferredLang !== this.authUser.lang &&
                !alreadyExists
            );
        },
        translateMessage() {
            if (!this.isChatWindow || this.isPrivateChat) {
                return false;
            }
            const contact = this.contact;
            if (contact?.preferredLang === this.authUser.lang) {
                this.$message.error("Müşteri ile aynı dilde olduğunuz için çeviri yapamazsınız.");
                return;
            }

            const alreadyExists = this.message.raw.translates.find(
                (t) => t.to === this.authUser.lang && t.from === contact?.preferredLang
            );
            console.log("translate message", {
                t: this.message.raw.translates,
                authUserLang: this.authUser.lang,
                preferredLang: contact?.preferredLang,
                alreadyExists,
            });
            if (alreadyExists) {
                this.$message.error("Bu mesaj daha önce zaten çevirildi.");
                return;
            }
            sendSocketMessage("user translate-message", {
                siteId: this.siteId,
                to: this.authUser.lang,
                data: {
                    ...this.message.raw,
                    baseLang: contact?.preferredLang,
                    message: this.message.raw.description || this.message.raw.message,
                    conversationId: this.message.raw.conversationId || this.message.raw.conversation,
                },
            });
        },
        runAutomation(automation) {
            const zohoMatch = automation.name.match(/\[(?:ZOHO|Zoho|zoho) ([^\]]+)\]/i);

            if (!zohoMatch) {
                // Normal otomasyon - Zoho ile ilgili değil
                sendSocketMessage("automation run-message-pick-automation", {
                    siteId: this.$store.state.site.site._id,
                    messageId: this.message.raw?._id || this.message._id || this.message.raw?.uniqMessageId,
                    automationId: automation._id,
                    userId: this.$store.state.auth.user?._id,
                });
                this.showContextMenu = false;
                return;
            }

            const module = zohoMatch[1]; // Deal veya başka bir modül

            if (module === "Deal") {
                // Mevcut Deal seçim kontrolü
                if (this.zohoSelectedDeals.length === 0) {
                    this.$message.error(
                        "Önce bir Zoho Deal seçmelisiniz. Sağ tarafdaki Zoho Widget'tan seçebilirsiniz."
                    );
                    this.showContextMenu = false;
                    return;
                }

                // Deal seçiliyse socket mesajı gönder
                sendSocketMessage("automation run-message-pick-automation", {
                    siteId: this.$store.state.site.site._id,
                    messageId: this.message.raw?._id || this.message._id || this.message.raw?.uniqMessageId,
                    automationId: automation._id,
                    zohoDealIds: this.zohoSelectedDeals,
                    userId: this.$store.state.auth.user._id,
                });
            } else {
                // Diğer Zoho modülleri için Record Picker'ı göster
                if (this.activeContactPhone.length === 0 && !this.activeContactZohoContactId) {
                    this.$message.error("Bu kişiye ait telefon numarası bulunamadı. / Zoho bilgisi bulunamadı.");
                } else {
                    this.currentAutomation = automation;
                    this.zohoModule = module;
                    this.showZohoRecordPicker = true;
                }
            }

            this.showContextMenu = false;
        },

        handleZohoRecordSelected(selectedRecords) {
            if (!this.currentAutomation || !selectedRecords.length) return;

            // Seçilen kayıtlarla socket mesajı gönder
            sendSocketMessage("automation run-message-pick-automation", {
                siteId: this.$store.state.site.site._id,
                messageId: this.message.raw?._id || this.message._id || this.message.raw?.uniqMessageId,
                automationId: this.currentAutomation._id,
                module: this.zohoModule,
                zohoRecordIds: selectedRecords.map((record) => record.id),
                userId: this.$store.state.auth.user?._id,
            });

            // Modal'ı kapat ve state'i temizle
            this.showZohoRecordPicker = false;
            this.currentAutomation = null;
            this.zohoModule = null;
        },

        handleZohoRecordPickerCancel() {
            this.showZohoRecordPicker = false;
            this.currentAutomation = null;
            this.zohoModule = null;
        },
        isRepliedMessage() {
            const conversationId = _.get(
                this.message,
                "raw.conversation",
                _.get(this.message, "raw.conversationId", null)
            );
            const replyMessageId = _.get(this.replyToMessages, `${conversationId}.raw.uniqMessageId`, null);
            return replyMessageId && replyMessageId === _.get(this.message, "raw.uniqMessageId", null);
        },
        isAgentTextMessage() {
            return ["private", "user"].includes(this.message.raw?.messageType);
        },
        isVisitorTextMessage() {
            return this.message.raw?.messageType === "visitor";
        },
        isImageMessage() {
            return this.message.type === "image";
        },
        isAssetMessage() {
            return this.message.type === "asset";
        },
        isCopyEnabled() {
            const isDeleted = this.message.raw?.isDeleted;
            return isDeleted
                ? false
                : this.isAgentTextMessage() || this.isVisitorTextMessage() || this.isImageMessage();
        },
        isDownloadEnabled() {
            return this.message.raw?.isDeleted ? false : this.isImageMessage() || this.isAssetMessage() || this.isVideoMessage();
        },
        downloadFile() {
            const downloadName = _.get(this.message, "raw.asset.name");
            const attachment = this.message.raw.asset;
            let url =
                attachment.v2Format && attachment.cdnUrl
                    ? attachment.cdnUrl
                    : `https://static.supsis.live/${attachment.path}`;
            if (!attachment.path.includes(".")) {
                url =
                    attachment.v2Format && attachment.cdnUrl
                        ? attachment.cdnUrl
                        : `https://static.supsis.live/${attachment.path + attachment._id}.${attachment.ext}`;
            }
            var hide = this.$message.loading(this.$t("message.downloading"), 0);
            var self = this;
            fetch(url)
                .then(function (res) { return res.blob(); })
                .then(function (blob) {
                    var blobUrl = URL.createObjectURL(blob);
                    var a = document.createElement("a");
                    a.href = blobUrl;
                    a.download = downloadName || "download";
                    a.click();
                    URL.revokeObjectURL(blobUrl);
                    hide();
                    self.$message.success(self.$t("message.downloadSuccess"));
                })
                .catch(function (err) {
                    console.error("Download failed", err);
                    hide();
                    self.$message.error(self.$t("message.downloadFailed"));
                });
        },
        isContextEnabled() {
            const isDeleted = this.message.raw?.isDeleted;
            if (isDeleted) return false;
            return [
                "user",
                "visitor",
                "visitor-location",
                "user-location",
                "private",
                "chatbot",
                "chatbot-private",
                "chatbot-image",
                "chatbot-file",
                "chatbot-text",
                "chatbot-button",
                "chatbot-quick-reply",
                "chatbot-gallery",
                "chatbot-get-location",
                "user-media",
                "visitor-media",
                "whatsapp-user",
                "whatsapp-user-media",
                "whatsapp-user-location",
                "instagram-user",
                "instagram-user-media",
                "instagram-user-location",
                "messenger-user",
                "messenger-user-media",
                "messenger-user-location",
                "visitor-json",
                "whatsapp-business-api-template",
            ].includes(this.message.raw?.messageType);
        },
        openContextMenu(event) {
            if (this.message.type === "conversation-separator") {
                this.separatorContextMenuPosition = {
                    x: event.clientX,
                    y: event.clientY,
                };
                this.showSeparatorContextMenu = true;
                return;
            }
            const clickX = event.clientX;
            const clickY = event.clientY;

            this.contextMenuPosition = { x: clickX, y: clickY };
            this.showContextMenu = true;

            this.$nextTick(() => {
                const msgId = _.get(this.message, "id") || _.get(this.message, "raw.uniqMessageId");
                const context = document.querySelector(
                    "#sc-message_" + msgId + " > .context-menu"
                );
                if (!context) return;
                const windowHeight = window.innerHeight;
                const windowWidth = window.innerWidth;
                const targetHeight = context.offsetHeight || 0;
                const targetWidth = context.offsetWidth || 0;

                this.contextMenuPosition = {
                    x: clickX + targetWidth > windowWidth ? windowWidth - targetWidth - 20 : clickX,
                    y: clickY + targetHeight > windowHeight ? windowHeight - targetHeight - 20 : clickY,
                };
            });
        },
        getAssetUrlFromMessage() {
            let asset = "";
            if (!!this.message.isMail) {
                asset = this.message;
            } else {
                asset = this.message.raw.asset;
            }
            const isWhatsapp = asset.path.indexOf("whatsapp") !== -1;
            const isInstagram = asset.path.indexOf("instagram") !== -1;
            const isMessenger = asset.path.indexOf("messenger") !== -1;
            const isTelegram = asset.path.indexOf("telegram") !== -1;
            const isLinkedin = asset.path.indexOf("linkedin") !== -1;
            const isApiMessages = asset.path.indexOf("api-messages") !== -1;

            const src =
                isWhatsapp || isInstagram || isMessenger || isTelegram || isLinkedin || isApiMessages
                    ? `https://static.supsis.live/${asset.path}`
                    : asset.v2Format && asset.cdnUrl
                    ? asset.cdnUrl
                    : `https://static.supsis.live/${asset.path + asset._id + (asset.ext ? "." + asset.ext : "")}`;
            return src;
        },
        copyChatId() {
            const chatId = this.getConversationId();
            copyText2Clipboard(chatId);
            this.$message.success("Chat ID copied to clipboard");
            this.showSeparatorContextMenu = false;
        },
        async onContextMenuSelected(command) {
            if (command === "copy") {
                if (this.message.type === "text" || this.message.type === "emoji") {
                    const regex = /<[^>]*(>|$)|&nbsp;|&zwnj;|&raquo;|&laquo;|&gt;/g;
                    const clearHTML = (string) => string?.replace(regex, "");
                    const string = this.message.raw.description || this.message.raw.message;
                    const sanitizedText = clearHTML(string);
                    copyText2Clipboard(sanitizedText);
                    this.$message.success("Copied to clipboard");
                } else if (this.message.type === "image") {
                    const url = this.getAssetUrlFromMessage();
                    await copyImage2Clipboard(url);
                    this.$message.success("Image copied to clipboard");
                }
            } else if (command === "translate") {
                this.translateMessage();
            } else if (command === "reply") {
                const isPrivate = ["chatbot-private", "private"].includes(this.message.raw.messageType);
                if (!isPrivate) {
                    this.pinReplyToMessage(this.message);
                } else {
                    this.$message.error(this.$t("auto.privateMessagesCannotQuote"));
                }
            }
            this.showContextMenu = false;
        },
        goToActorBlock() {
            const detail = this.message.raw?.actorDetail;
            if (!detail) return;

            let url = "";
            if (detail.type === "automation") {
                url = `/automations/${detail.automation}/design${detail.blockId ? "/block/" + detail.blockId : ""}`;
            } else if (detail.type === "chatbot") {
                const isAIAssistant = detail.blockId && (detail.blockId === "ai_assistant" || detail.blockId === "AI_ASSISTANT");
                url = `/chatbot/story/${detail.chatbotStory}/${detail.chatbotStoryVersion}${detail.blockId && !isAIAssistant ? "/block/" + detail.blockId : ""}`;
            }

            if (url) {
                this.$router.push(url);
            }
            this.showContextMenu = false;
        },
        isWABusiness() {
            const channel = this.channels.find((c) => c._id === this.channelId);
            return _.get(channel, "type") === "whatsapp-business-api" || _.get(channel, "type") === "whatsapp-business-api-supsis";
        },
        isWhatsappV3() {
            const channel = this.channels.find((c) => c._id === this.channelId);
            return _.get(channel, "type") === "whatsapp-unipile";
        },
        isInstagram() {
            const channel = this.channels.find((c) => c._id === this.channelId);
            return ["instagram", "instagram-v2"].includes(_.get(channel, "type"));
        },
        isMessenger() {
            const channel = this.channels.find((c) => c._id === this.channelId);
            return _.get(channel, "type") === "messenger";
        },
        isLinkedin() {
            const channel = this.channels.find((c) => c._id === this.channelId);
            return _.get(channel, "type") === "linkedin-unipile";
        },
        isTelegram() {
            const channel = this.channels.find((c) => c._id === this.channelId);
            return _.get(channel, "type") === "telegram-unipile";
        },
        isCommentMessage() {
            const contact = this.contact;
            if (!contact) return false;

            const platform = contact.platform;
            if (!["instagram", "messenger"].includes(platform)) return false;

            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            const lastCommentId = _.get(contact, `${infoField}.lastCommentId`);
            const externalProviderMessageId = _.get(this.message, "raw.externalProviderMessageId") || _.get(this.message, "raw.metaMessageId");

            // Son comment mesajı ise true döner (cevap verilmiş olsa bile context menü gözüksün)
            return lastCommentId && externalProviderMessageId && lastCommentId === externalProviderMessageId &&
                   ["visitor", "visitor-media"].includes(_.get(this.message, "raw.messageType"));
        },
        replyCommentAction() {
            // Reply to message olarak pin'le
            this.$emit("reply-comment-action", {
                commentId: _.get(this.message, "raw.externalProviderMessageId") || _.get(this.message, "raw.metaMessageId"),
                replyToMessage: this.message,
            });
            this.showContextMenu = false;
        },
        createTask() {
            this.addNewWindow({
                type: "chat-create-task",
                title: this.$t("task.createTaskMessage"),
                payload: {
                    visitorId: this.visitorId,
                    formDefaults: {
                        title: this.message.data.text,
                    },
                    //geçici süreliğine kaldırıldı
                    // messageId: this.message.raw._id,
                    // conversationId: this.getConversationId(),
                },
            });
        },
        getConversationId() {
            return (
                _.get(this.message, "raw.conversation._id") ||
                _.get(this.message, "raw.conversation") ||
                _.get(this.message, "raw.conversationId")
            );
        },
        whatsappDeliveryStatus() {
            const uniqMessageId = _.get(this.message, "raw.uniqMessageId");
            const isSentFromPhone = ["whatsapp-user", "whatsapp-user-media"].includes(
                _.get(this.message, "raw.messageType")
            );
            const isBusiness = this.isWABusiness();
            const isWhatsappV3 = this.isWhatsappV3();
            const isLinkedin = this.isLinkedin();
            const isTelegram = this.isTelegram();
            const isSentMessage = (this.isWhatsapp || isBusiness || isWhatsappV3) && this.message.position === "right";
            const isPending =
                _.get(this.message, "raw.status", "sent") === "pending" ||
                !!this.pendingWhatsappMessages[uniqMessageId];
            const isDelivered =
                _.get(this.message, "raw.whatsappDelivered", false) === true ||
                !!this.resentWhatsappMessageStatus[uniqMessageId];

            if (isBusiness) {
                const lastWhatsappBusinessStatus = _.get(this.message, "raw.lastWhatsappBusinessStatus");
                const isEnqueued = _.get(lastWhatsappBusinessStatus, "status") === "enqueued";
                const statusExists = _.get(lastWhatsappBusinessStatus, "status");

                // Error reason - Gupshup ve Meta WABA uyumluluğu için
                const errorReason =
                    _.get(lastWhatsappBusinessStatus, "reason") ||
                    _.get(lastWhatsappBusinessStatus, "error.message") ||
                    _.get(lastWhatsappBusinessStatus, "error.title") ||
                    _.get(lastWhatsappBusinessStatus, "error.error_data.details") ||
                    "Bilinmeyen hata";

                const isNativeDelivered = !statusExists && isDelivered;
                const result = {
                    visible: this.message.position === "right",
                    pending: !!statusExists ? isPending || isEnqueued : !isNativeDelivered,
                    delivered: isNativeDelivered || ["read", "sent", "delivered", "delete"].includes(
                        _.get(lastWhatsappBusinessStatus, "status")
                    ),
                    tooltip: (!!statusExists ? isPending || isEnqueued : !isNativeDelivered)
                        ? isEnqueued
                            ? "⚪️ Mesaj kuyruğa alındı"
                            : "⚪️ Mesaj gönderiliyor"
                            : isNativeDelivered
                            ? "🟢 Mesaj uygulamadan gönderildi"
                            : {
                              enqueued: "⚪️ Mesaj kuyruğa alındı",
                              failed: `🔴 Mesaj gönderilemedi - ` + errorReason,
                              read: "🟢 Mesaj okundu",
                              sent: "🟢 Mesaj gönderildi",
                              delivered: "🟢 Mesaj teslim edildi",
                              delete: "🟠 Mesaj silindi",
                          }[_.get(lastWhatsappBusinessStatus, "status")],
                };

                return result;
            } else if (isWhatsappV3) {
                // Whatsapp V3 (Unipile) için lastUnipileStatus kullan
                const lastUnipileStatus = _.get(this.message, "raw.lastUnipileStatus", "sent");
                const isDeleted = lastUnipileStatus === "deleted";

                const result = {
                    // deleted durumunda hem sağ hem sol tarafta göster, diğer durumlar sadece sağ tarafta
                    visible: isDeleted ? true : this.message.position === "right",
                    pending: lastUnipileStatus === "sent",
                    delivered: ["delivered", "read"].includes(lastUnipileStatus),
                    tooltip:
                        {
                            sent: "🟠 Mesaj Supsis QR sistemine iletildi",
                            delivered: "🟢 Mesaj müşteriye iletildi",
                            read: "🟢 Mesaj müşteri tarafından okundu",
                            deleted: "🔴 Mesaj müşteri tarafından silindi",
                        }[lastUnipileStatus] || "🟠 Mesaj Supsis QR sistemine iletildi",
                };

                return result;
            } else if (isLinkedin) {
                const lastUnipileStatus = _.get(this.message, "raw.lastUnipileStatus", "sent");
                const isDeleted = lastUnipileStatus === "deleted";

                return {
                    visible: isDeleted ? true : this.message.position === "right",
                    pending: lastUnipileStatus === "sent",
                    delivered: ["delivered", "read"].includes(lastUnipileStatus),
                    tooltip:
                        {
                            sent: "🟠 Mesaj LinkedIn'a gönderildi",
                            delivered: "🟢 Mesaj müşteriye iletildi",
                            read: "🟢 Mesaj müşteri tarafından okundu",
                            deleted: "🔴 Mesaj müşteri tarafından silindi",
                        }[lastUnipileStatus] || "🟠 Mesaj Linkedin'e iletildi",
                };
            } else if (isTelegram) {
                const lastUnipileStatus = _.get(this.message, "raw.lastUnipileStatus", "sent");
                const isDeleted = lastUnipileStatus === "deleted";

                return {
                    visible: isDeleted ? true : this.message.position === "right",
                    pending: lastUnipileStatus === "sent",
                    delivered: ["delivered", "read"].includes(lastUnipileStatus),
                    tooltip:
                        {
                            sent: "🟠 Mesaj Telegram'a gönderildi",
                            delivered: "🟢 Mesaj müşteriye iletildi",
                            read: "🟢 Mesaj müşteri tarafından okundu",
                            deleted: "🔴 Mesaj müşteri tarafından silindi",
                        }[lastUnipileStatus] || "🟠 Mesaj Telegram'a iletildi",
                };
            } else {
                return {
                    visible: !isSentFromPhone && isSentMessage,
                    pending: isPending,
                    delivered: isDelivered,
                    tooltip: isPending
                        ? "⚪️ Mesaj WhatsApp tarafına gönderiliyor"
                        : isDelivered
                        ? "🟢 Mesaj WhatsApp tarafına gönderildi"
                        : "🔴 Mesaj WhatsApp tarafına gönderilemedi. Tekrar göndermek için tıklayınız...",
                };
            }
        },
        metaDeliveryStatus() {
            const uniqMessageId = _.get(this.message, "raw.uniqMessageId");
            const isPrivateMessage = ["chatbot-private", "private"].includes(_.get(this.message, "raw.messageType"));
            const isSentFromNative = [
                "instagram-user",
                "instagram-user-media",
                "messenger-user",
                "messenger-user-media",
            ].includes(_.get(this.message, "raw.messageType"));
            const isInstagram = this.isInstagram();
            const isMessenger = this.isMessenger();
            const isSentMessage = (isInstagram || isMessenger) && this.message.position === "right";
            const metaMessageStatus = _.get(this.message, "raw.metaMessageStatus", "sent");

            const result = {
                visible: !isSentFromNative && isSentMessage && !isPrivateMessage,
                pending: metaMessageStatus === "sent",
                delivered: ["delivered", "read"].includes(metaMessageStatus),
                tooltip:
                    {
                        sent: isInstagram ? "🟠 Mesaj Instagram'a gönderildi" : "🟠 Mesaj Messenger'a gönderildi",
                        delivered: isInstagram
                            ? "🟢 Mesaj Instagram'da teslim edildi"
                            : "🟢 Mesaj Messenger'da teslim edildi",
                        read: isInstagram ? "🟢 Mesaj Instagram'da okundu" : "🟢 Mesaj Messenger'da okundu",
                        deleted: isInstagram ? "🔴 Mesaj Instagram'da silindi" : "🔴 Mesaj Messenger'da silindi",
                        edited: isInstagram ? "🟡 Mesaj Instagram'da düzenlendi" : "🟡 Mesaj Messenger'da düzenlendi",
                    }[metaMessageStatus] ||
                    (isInstagram ? "🟠 Mesaj Instagram'a gönderildi" : "🟠 Mesaj Messenger'a gönderildi"),
                platform: isInstagram ? "instagram" : "messenger",
            };

            return result;
        },
        ...mapMutations({
            removePendingMsg: "chat/REMOVE_PENDING_MESSAGE",
        }),
        updateWhatsappPendingMessage(uniqMessageId, status) {
            if (status) {
                this.pendingWhatsappMessages[uniqMessageId] = true;
            } else {
                delete this.pendingWhatsappMessages[uniqMessageId];
            }
            this.pendingWhatsappMessages = _.cloneDeep(this.pendingWhatsappMessages);
        },
        updateResentWhatsappMessageStatus(uniqMessageId, status) {
            this.resentWhatsappMessageStatus[uniqMessageId] = status;
            this.resentWhatsappMessageStatus = _.cloneDeep(this.resentWhatsappMessageStatus);
        },
        resendWhatsappMessage() {
            const status = this.whatsappDeliveryStatus();
            if (status.pending || status.delivered) return;
            const uniqMessageId = _.get(this.message, "raw.uniqMessageId");
            sendSocketMessage("user resend-whatsapp-message", {
                siteId: this.siteId,
                userId: this.authUser._id,
                uniqMessageId,
                message: _.get(this.message, "raw.description") || _.get(this.message, "raw.message"),
                conversationId: this.getConversationId(),
                asset: _.get(this.message, "raw.asset"),
                location: _.get(this.message, "raw.location"),
            });
            this.updateWhatsappPendingMessage(uniqMessageId, true);
            socketMessageStream
                .pipe(
                    filter(({ cmd, data }) => cmd === "whatsapp_message_delivery_status_update"),
                    filter(({ cmd, data }) => data.uniqMessageId === uniqMessageId),
                    take(1),
                    timeout(10000),
                    first()
                )
                .toPromise()
                .then(({ cmd, data }) => {
                    const { whatsappDelivered } = data;
                    this.updateWhatsappPendingMessage(uniqMessageId, false);
                    if (whatsappDelivered) {
                        this.updateResentWhatsappMessageStatus(uniqMessageId, true);
                    } else {
                        this.updateResentWhatsappMessageStatus(uniqMessageId, false);
                    }
                });
        },
        isAudioMessage() {
            const isAudioType = this.message.type === "audio";
            const isAudioMime = _.get(this.message, "raw.asset.mime", "").includes("audio");
            return isAudioType || isAudioMime;
        },
        isVideoMessage() {
            const mime = _.get(this.message, "raw.asset.mime", "") || "";
            const ext = (_.get(this.message, "raw.asset.ext", "") || "").toLowerCase();
            const videoExts = ["mp4", "webm", "mov", "avi", "mkv", "m4v"];
            const isVideoByMime = mime.includes("video");
            const isVideoByExt = videoExts.includes(ext);
            const isVideoType = this.message.type === "video";
            return (isVideoType || isVideoByMime || isVideoByExt) && !["meta-post"].includes(this.message.type);
        },
        receivedColorsStyle() {
            const isDeleted = this.message.raw?.isDeleted;
            return {
                color: isDeleted ? "var(--color-text-critical-default)" : this.colors.receivedMessage.text,
                backgroundColor: this.colors.receivedMessage.bg,
            };
        },
        standartColorStyle() {
            return {
                color: this.colors.sentMessage.text,
                backgroundColor: this.colors.sentMessage.bg,
            };
        },
        systemMessageColorsStyle() {
            return {
                color: "#FD6144",
                backgroundColor: "#FFEEEB",
            };
        },
        privateColorsStyle() {
            return {
                color: this.colors.receivedMessage.text,
                backgroundColor: "rgb(253 255 104)",
                opacity: "0.5",
            };
        },
        sentColorsStyle() {
            const colors = this.userMessageColor;
            return {
                color: colors.color,
                backgroundColor: colors.backgroundColor,
            };
        },
        extendedStyle() {
            const style = {
                width: "auto",
                minWidth: "125px",
                maxWidth: "calc(100% - 60px) !important",
            };
            return _.merge(style, this.overrideStyle || {});
        },
        isNewMessage(message) {
            return (
                this.beforeLastPrivateChat === message.id && message.author !== "me" && !this.isPrivateChatPositionLast
            );
        },
        shouldShowDismissMsg() {
            if (!!this.timer) clearTimeout(this.timer);
            let threshold = 30;
            const createdTS = this.message.raw.createdDate;
            const cancelExpireTime = moment(createdTS).add(threshold, "seconds").valueOf();
            const waitTimeForCancelInMs = Math.min(Math.max(0, cancelExpireTime - Date.now()), threshold * 1000);

            this.timer = setTimeout(() => {
                this.shouldDismissMessage = true;
            }, waitTimeForCancelInMs);
        },
        handleErrorClick() {
            const status = this.whatsappDeliveryStatus();
            //if (!status.delivered && !status.pending) {
            this.currentError = _.get(this.message, "raw.lastWhatsappBusinessStatus.reason", "");
            this.showErrorModal = true;
            //}
        },
        getTotalReactionsTooltip() {
            if (this.totalReactionsCount === 1) {
                return "1 reaksiyon - detayları görmek için tıklayın";
            }
            return `${this.totalReactionsCount} reaksiyon - detayları görmek için tıklayın`;
        },
        openReactionModal() {
            this.showReactionModal = true;
        },
        formatReactionTime(timestamp) {
            if (!timestamp) return "";
            return this.$moment(timestamp).format("DD MMMM YYYY, HH:mm");
        },
        getEditedText() {
            const editedAt = this.message.raw?.editedAt;
            if (!editedAt) {
                return "Düzenlendi";
            }
            const formattedTime = this.$moment(editedAt).format("HH:mm");
            const isToday = this.$moment(editedAt).isSame(this.$moment(), "day");

            if (isToday) {
                return `${formattedTime}'de düzenlendi`;
            } else {
                const formattedDate = this.$moment(editedAt).format("DD.MM.YYYY HH:mm");
                return `${formattedDate}'de düzenlendi`;
            }
        },
        handleSubmenuHover(event, categoryName) {
            this.cancelSubmenuLeave();
            // Önce tüm sub menüleri temizle
            this.currentSubmenuPosition = {};
            // Sonra sadece bu kategori için pozisyon set et
            const rect = event.currentTarget.getBoundingClientRect();
            this.$set(this.currentSubmenuPosition, categoryName, {
                top: `${rect.top}px`,
                left: `${rect.right}px`,
                paddingLeft: "8px",
            });
        },
        handleSubmenuLeave() {
            this.submenuLeaveTimeout = setTimeout(() => {
                this.currentSubmenuPosition = {};
            }, 100);
        },
        cancelSubmenuLeave() {
            if (this.submenuLeaveTimeout) {
                clearTimeout(this.submenuLeaveTimeout);
                this.submenuLeaveTimeout = null;
            }
        },
    },
    mounted() {
        if (_.get(this.message, "raw.status") === "pending") this.shouldShowDismissMsg();
        this.contextMenuTarget = this.$refs.contextWrapper;
    },
    beforeDestroy() {
        if (!!this.timer) clearTimeout(this.timer);
    },

    watch: {
        message() {
            if (_.get(this.message, "raw.status") === "pending") this.shouldShowDismissMsg();
            else {
                this.shouldDismissMessage = false;
                if (!!this.timer) clearTimeout(this.timer);
            }
        },
    },
};
</script>

<style lang="scss">
.sc-message-wrapper {
    display: flex;
    //position: relative;
    //flex-direction: row-reverse;
    //justify-content: center;
    //align-items: flex-end;
    position: relative;
    justify-content: center;
    margin-bottom: 8px; // Reaction'lar için yer bırak

    &.repliedMessage {
        border-left: 4px solid var(--color-reply-message-border);
        background: var(--color-reply-bg);
        border-radius: 6px;
    }

    .vue2leaflet-map {
        z-index: 0 !important;
    }

    .context-menu {
        position: fixed;
        background: var(--color-bg-neutral-default);
        border: solid 1px rgba(0, 0, 0, 0.2);
        border-radius: 3px;
        z-index: 999;
        min-width: 150px;

        .context-menu-content {
            display: flex;
            flex-direction: column;
            gap: 5px;
            width: 100%;

            .item-divider {
                height: 1px;
                background: var(--color-border-default);
                margin: 5px 0;
            }

            .item {
                padding: 5px 10px;
                cursor: pointer;
                border-bottom: 1px solid var(--color-border-default);
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 5px;

                .material-icons {
                    font-size: 15px;
                }

                &:hover {
                    background: var(--color-bg-neutral-low-hover);
                }
            }

            .submenu-item {
                position: relative;

                .submenu-arrow {
                    margin-left: auto;
                    font-size: 16px;
                }

                .submenu-content {
                    position: fixed;
                    z-index: 1001;

                    .submenu-inner {
                        min-width: 180px;
                        background: var(--color-bg-neutral-default);
                        border: solid 1px rgba(0, 0, 0, 0.2);
                        border-radius: 3px;

                        .item {
                            border-bottom: 1px solid var(--color-border-default);

                            &:last-child {
                                border-bottom: none;
                            }
                        }
                    }
                }
            }
        }
    }
}

.toolbox-translate {
    font-size: 12px;
    position: absolute;
    background: #fff;
    z-index: 2;
    left: 20px;
    top: -24px;
    opacity: 0;
    transition: all 0.3s;
    padding: 5px 10px;
    border-radius: 12px;
    box-shadow: 0 0 3px #00000080;
    color: #f1f9fc;
    cursor: pointer;
    user-select: none;
}

.sc-message--content {
    .sc-message--text:hover .toolbox-translate {
        opacity: 1;
    }
}

.sc-message {
    width: 100%;
    margin: auto;
    //padding-bottom: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    cursor: pointer;

    &:hover,
    &.selected {
        background: var(--color-bg-neutral-low-hover);
    }

    &.load-more {
        &:hover {
            background: transparent;
        }
    }

    .wp-delivery {
        cursor: pointer;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 11px;
        flex-direction: row;
        margin-top: 4px;
        gap: 4px;
        max-width: 500px;
        justify-content: flex-end;
        flex-wrap: wrap;

        &.wp-delivery-left {
            align-self: baseline !important;
        }

        &:hover {
            text-decoration: underline;
            cursor: pointer;
        }

        .wp-delivery-icon {
            width: 15px;
            height: 15px;

            svg {
                width: 15px;
                height: 15px;
            }

            &.instagram {
                svg {
                    fill: #ff00cd !important;

                    path {
                        fill: #ff00cd !important;
                    }
                }

                .ig-background {
                    fill: #ff00cd !important;
                }
            }
        }

        .delivery-status {
            font-size: 11px;
            white-space: nowrap;
            word-wrap: break-word;
        }

        .resent-label {
            font-style: normal;
            margin-left: 4px;
        }
    }

    .daysDivider {
        color: #969696;
        font-size: 14px;
    }

    .sc-message--edited {
        opacity: 0.7;
        word-wrap: normal;
        font-size: xx-small;
        text-align: center;
    }
}

.sc-message--content {
    position: relative;
    width: 100%;
    display: flex;
}

.sc-message--content.sent {
    justify-content: flex-end;

    .asset-text {
        color: #ffffff !important;
    }
}

.sc-message--content.sent {
    a.chatLink.chatLink-email {
        color: #ffffff !important;
    }
}

.sc-message--content.system {
    justify-content: center;
}

.sc-message--content.sent .sc-message--avatar {
    display: none;
}

.sc-message--avatar {
    background-repeat: no-repeat;
    background-size: 100%;
    background-position: center;
    min-width: 30px;
    min-height: 30px;
    border-radius: 50%;
    align-self: center;
    margin-right: 15px;
    font: 15px / 20px Helvetica, Arial, sans-serif !important;
}

.sc-message--meta {
    font-size: 8px;
    font-weight: 300;
    margin-bottom: 0px;
    color: white;
    text-align: left;
    text-transform: uppercase;
    position: absolute;
    bottom: -1px;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.sc-message--meta2 {
    font-size: 8px;
    font-weight: 300;
    margin-bottom: 0px;
    color: white;
    text-align: left;
    text-transform: capitalize;
}

@media (max-width: 450px) {
    .sc-message {
        width: 80%;
    }
}

.sc-message--content {
    &.received {
        .sc-message--text {
            border-radius: 6px 6px 6px 0px;
        }
    }

    &.sent {
        .sc-message--text {
            border-radius: 6px 6px 0px 6px;
        }
    }
}

.sc-message--text {
    //padding: 5px 30px 17px 15px;
    border-radius: 6px;
    /*font-weight: 300;*/
    font-size: 14px;
    line-height: 1.4;
    position: relative;
    -webkit-font-smoothing: subpixel-antialiased;

    /* .sc-message--text-body {
   .sc-message--text-content {
       !*white-space: pre-wrap;*!
       margin-bottom: 0 !important;
   }
}*/
    .sc-message--text-content {
        p:last-child {
            margin: 0 !important;
        }
    }

    &:hover .sc-message--toolbox {
        left: -20px;
        opacity: 1;
    }

    .sc-message--toolbox {
        transition: left 0.2s ease-out 0s;
        white-space: normal;
        opacity: 0;
        position: absolute;
        left: 0px;
        width: 25px;
        top: 0;

        button {
            background: none;
            border: none;
            padding: 0px;
            margin: 0px;
            outline: none;
            width: 100%;
            text-align: center;
            cursor: pointer;

            &:focus {
                outline: none;
            }
        }
    }
}

.sc-message--content.sent .sc-message--text {
    color: white;
    background-color: #4e8cff;
    max-width: calc(100% - 120px);
    word-wrap: break-word;
    font: 15px / 20px Helvetica, Arial, sans-serif;
}

.sc-message--text code {
    font-family: "Courier New", Courier, monospace !important;
}

.sc-message--content.received .sc-message--text {
    color: #263238;
    background-color: #f4f7f9;
    margin-right: 40px;
    margin-bottom: 0 !important;
    font: 15px / 20px Helvetica, Arial, sans-serif;
}

.tooltip {
    display: block !important;
    z-index: 10000;

    .tooltip-inner {
        background: black;
        color: white;
        border-radius: 16px;
        padding: 5px 10px 4px;
    }

    .tooltip-arrow {
        width: 0;
        height: 0;
        border-style: solid;
        position: absolute;
        margin: 5px;
        border-color: black;
        z-index: 1;
    }

    &[x-placement^="top"] {
        margin-bottom: 5px;

        .tooltip-arrow {
            border-width: 5px 5px 0 5px;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            border-bottom-color: transparent !important;
            bottom: -5px;
            left: calc(50% - 5px);
            margin-top: 0;
            margin-bottom: 0;
        }
    }

    &[x-placement^="bottom"] {
        margin-top: 5px;

        .tooltip-arrow {
            border-width: 0 5px 5px 5px;
            border-left-color: transparent !important;
            border-right-color: transparent !important;
            border-top-color: transparent !important;
            top: -5px;
            left: calc(50% - 5px);
            margin-top: 0;
            margin-bottom: 0;
        }
    }

    &[x-placement^="right"] {
        margin-left: 5px;

        .tooltip-arrow {
            border-width: 5px 5px 5px 0;
            border-left-color: transparent !important;
            border-top-color: transparent !important;
            border-bottom-color: transparent !important;
            left: -5px;
            top: calc(50% - 5px);
            margin-left: 0;
            margin-right: 0;
        }
    }

    &[x-placement^="left"] {
        margin-right: 5px;

        .tooltip-arrow {
            border-width: 5px 0 5px 5px;
            border-top-color: transparent !important;
            border-right-color: transparent !important;
            border-bottom-color: transparent !important;
            right: -5px;
            top: calc(50% - 5px);
            margin-left: 0;
            margin-right: 0;
        }
    }

    &[aria-hidden="true"] {
        visibility: hidden;
        opacity: 0;
        transition: opacity 0.15s, visibility 0.15s;
    }

    &[aria-hidden="false"] {
        visibility: visible;
        opacity: 1;
        transition: opacity 0.15s;
    }

    &.info {
        $color: rgba(#004499, 0.9);

        .tooltip-inner {
            background: $color;
            color: white;
            padding: 24px;
            border-radius: 5px;
            box-shadow: 0 5px 30px rgba(black, 0.1);
        }

        .tooltip-arrow {
            border-color: $color;
        }
    }

    &.popover {
        $color: #f9f9f9;

        .popover-inner {
            background: $color;
            color: black;
            padding: 24px;
            border-radius: 5px;
            box-shadow: 0 5px 30px rgba(black, 0.1);
        }

        .popover-arrow {
            border-color: $color;
        }
    }
}

.newMessageDivider {
    margin: 5px !important;
    color: red !important;
    font-weight: 400 !important;
    font-size: 14px !important;
}

.separator-context-menu {
    position: fixed;
    background: var(--color-bg-neutral-default);
    border: solid 1px rgba(0, 0, 0, 0.2);
    border-radius: 3px;
    z-index: 999;
    min-width: 160px;

    .item {
        padding: 5px 10px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 5px;

        .material-icons {
            font-size: 15px;
        }

        &:hover {
            background: var(--color-bg-neutral-low-hover);
        }
    }
}

.checkMark {
    position: absolute;
    bottom: 5px;
    right: 3px;
}

.notSend {
    width: 24px;
    height: 24px;
    background: red;
    border-radius: 50%;
    color: #fff;
    text-align: center;
    line-height: 24px;
    margin: 1rem;
    cursor: pointer;
}

.sc-message--content {
    position: relative; // Bu önemli - child absolute positioning için
}

.message-reactions {
    position: absolute;
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 13px;
    padding: 2px 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    min-height: 26px;
    gap: 2px;
    z-index: 10;
    bottom: -8px;

    &:hover {
        background: rgba(255, 255, 255, 1);
        transform: scale(1.05);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &.reactions-right {
        right: 8px;
    }

    &.reactions-left {
        right: 0px;
        border: 1px solid #f2edff;
        opacity: 0.7;

        &:hover {
            opacity: 1;
        }
    }

    .reaction-emoji {
        font-size: 16px;
        line-height: 1;
        display: inline-block;
        margin-right: 1px;
    }

    .reaction-total-count {
        font-size: 13px;
        font-weight: 600;
        color: #65676b;
        margin-left: 4px;
        line-height: 1;
    }
}

// Modal styles
.reactions-modal-content {
    max-height: 400px;
    overflow-y: auto;

    .reaction-group {
        margin-bottom: 20px;

        &:last-child {
            margin-bottom: 0;
        }

        .reaction-group-header {
            display: flex;
            align-items: center;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid var(--color-border-default);

            .reaction-group-emoji {
                font-size: 20px;
                margin-right: 8px;
            }

            .reaction-group-count {
                font-size: 14px;
                font-weight: 600;
                color: var(--color-text-secondary-default);
            }
        }

        .reaction-users-list {
            .reaction-user {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 12px 16px;
                border-bottom: 1px solid var(--color-border-subtle);
                background: var(--color-bg-neutral-subtle);
                border-radius: 8px;
                margin-bottom: 8px;

                &:last-child {
                    margin-bottom: 0;
                }

                .reaction-user-info {
                    display: flex;
                    flex-direction: column;
                    flex: 1;

                    .reaction-user-name {
                        font-size: 15px;
                        font-weight: 600;
                        color: var(--color-text-primary-default);
                        margin-bottom: 4px;
                    }

                    .reaction-timestamp {
                        font-size: 13px;
                        color: var(--color-text-secondary-default);
                        font-weight: 400;
                    }
                }
            }
        }
    }
}

// Message Edited Indicator Styles
.message-edited-indicator {
    position: absolute;
    bottom: -20px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: var(--color-text-secondary-default);
    opacity: 0.7;
    z-index: 5;

    &.edited-right {
        right: 8px;
    }

    &.edited-left {
        left: 8px;
    }

    .material-icons {
        font-size: 14px;
        color: var(--color-text-secondary-default);
    }

    .edited-text {
        font-style: italic;
        white-space: nowrap;
    }

    &:hover {
        opacity: 1;
    }
}

// Adjust message wrapper margin for edited indicator
.sc-message-wrapper {
    &:has(.message-edited-indicator) {
        margin-bottom: 20px;
    }
}

// Chatbot debug mesajı için özel stiller
.sc-message.chatbot-debug {
    &:hover {
        background: transparent !important;
    }

    .checkMark {
        display: none !important;
    }

    .sc-message--content {
        overflow: visible !important;
    }
}

.ai-model-info {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    font-size: 10px;
    color: #8696a0;
    opacity: 0.8;
    margin-top: 2px;
    width: 100%;
    justify-content: flex-end;

    .material-icons {
        font-size: 12px;
        color: #722ed1;
    }

    &.received {
        justify-content: flex-start;
    }
}

.sc-message-fallback-meta {
    display: flex;
    width: 100%;
    padding: 2px 8px 0;

    &.fallback-meta-right {
        justify-content: flex-end;
    }

    &.fallback-meta-left {
        justify-content: flex-start;
    }

    .fallback-meta-text {
        font-size: 9px;
        font-weight: 300;
        color: #8696a0;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }
}
</style>
